import { ChevronDown, ChevronUp, ChevronsUpDown, Funnel } from "lucide-react";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";

import ColumnFilterPopover from "./ColumnFilterPopover";
import ColumnTextFilterPopover from "./ColumnTextFilterPopover";
import { ColumnId, SortableField, SortOrder } from "./types";
import { HEALTH_INDEX_OPTIONS } from "../../../config";

const SORT_FIELD_LABEL_KEYS_TRANS: Record<string, string> = {
  name: "facility_name",
  "stats.score": "health_index",
  average_rating: "average_rating",
  total_review_count: "total_review_count",
  "stats.profile_views": "profile_views",
  "stats.profile_views_change_percentage": "profile_views_change_percentage",
  "stats.direction_search_count": "direction_search_count",
  "stats.direction_search_count_change_percentage":
    "direction_search_count_change_percentage",
  prefecture: "municipality",
  city: "municipality",
  category: "category",
  labels: "labels",
};

interface RefactoredFacilityTableHeaderFilterProps {
  activeSortField: SortableField;
  activeSortOrder: SortOrder;
  visibleColumns: ColumnId[];
  onSelectSortField: (field: SortableField) => void;
  healthIndexData: any[];
  categoriesData: any[];
  cityFilterKeyword: string;
  prefectureFilterKeyword: string;
  onApplyFilter: (columnId: string, value: any[] | string) => void;
  onClearFilter: (columnId: string) => void;
}

const RefactoredFacilityTableHeaderFilter: React.FunctionComponent<
  RefactoredFacilityTableHeaderFilterProps
> = ({
  activeSortField,
  activeSortOrder,
  visibleColumns,
  onSelectSortField,
  healthIndexData,
  categoriesData,
  cityFilterKeyword,
  prefectureFilterKeyword,
  onApplyFilter,
  onClearFilter,
}) => {
  const { t: _t } = useTranslation();
  const t = (key: string) => _t(`facilityListPage.facilityList.${key}`);
  const tFilter = (key: string) =>
    _t(`facilityListPage.modalFilterCondition.${key}`);

  const [activeFilterColumn, setActiveFilterColumn] = useState<string | null>(
    null
  );

  const getSortableFieldForColumn = (
    columnId: ColumnId
  ): SortableField | null => {
    switch (columnId) {
      case "name":
        return "name";
      case "average_rating":
        return "average_rating";
      case "total_review_count":
        return "total_review_count";
      case "health_index":
        return "stats.score";
      case "profile_views":
        return "stats.profile_views";
      case "profile_views_change":
        return "stats.profile_views_change_percentage";
      case "direction_search":
        return "stats.direction_search_count";
      case "direction_search_change":
        return "stats.direction_search_count_change_percentage";
      case "prefecture":
        return "prefecture";
      case "city":
        return "city";
      case "category":
        return "category";
      default:
        return null;
    }
  };

  const createSortButton = (columnId: ColumnId, labelText: string) => {
    const sortField = getSortableFieldForColumn(columnId);
    if (!sortField) {
      return <span className="column-label-text">{labelText}</span>;
    }

    const isActive = activeSortField === sortField;

    return (
      <button
        type="button"
        className={`refactored-sort-button ${isActive ? "active" : ""}`}
        onClick={() => {
          onSelectSortField(sortField);
        }}
      >
        <span>{labelText}</span>
        <span
          className={`sort-icon-wrap ${isActive ? "is-active" : "is-idle"}`}
          aria-hidden="true"
        >
          {isActive ? (
            activeSortOrder === "desc" ? (
              <ChevronDown className="sort-icon" size={14} strokeWidth={2} />
            ) : (
              <ChevronUp className="sort-icon" size={14} strokeWidth={2} />
            )
          ) : (
            <ChevronsUpDown className="sort-icon" size={14} strokeWidth={2} />
          )}
        </span>
      </button>
    );
  };

  const getFilterOptionsForColumn = (columnId: ColumnId) => {
    switch (columnId) {
      case "health_index":
        return HEALTH_INDEX_OPTIONS.map((opt) => {
          const displayLabel = tFilter(`healthIndexLabels.${opt.key}`);
          const isChecked = healthIndexData.some(
            (h) => h.checked && h.name === displayLabel
          );
          return {
            label: displayLabel,
            value: opt.key,
            checked: isChecked,
          };
        });
      case "category":
        return categoriesData.map((cat) => ({
          label: cat.name,
          value: cat.id,
          checked: !!cat.checked,
        }));
      default:
        return [];
    }
  };

  const renderFilterTrigger = (columnId: ColumnId) => {
    const isFilterable = [
      "health_index",
      "prefecture",
      "city",
      "category",
    ].includes(columnId);

    if (!isFilterable) {
      return null;
    }

    const isTextFilter = columnId === "city" || columnId === "prefecture";
    const options = isTextFilter ? [] : getFilterOptionsForColumn(columnId);
    const hasActiveFilter = isTextFilter
      ? columnId === "city"
        ? cityFilterKeyword.trim().length > 0
        : prefectureFilterKeyword.trim().length > 0
      : options.some((opt) => opt.checked);

    return (
      <div className="filter-trigger-wrap">
        <button
          type="button"
          className={`filter-trigger-btn ${
            hasActiveFilter ? "has-filter" : ""
          }`}
          onClick={(e) => {
            e.stopPropagation();
            setActiveFilterColumn((prev) =>
              prev === columnId ? null : columnId
            );
          }}
          aria-label={`filter-${columnId}`}
        >
          <Funnel className="funnel-icon" size={14} strokeWidth={2} />
        </button>
        {activeFilterColumn === columnId &&
          (isTextFilter ? (
            <ColumnTextFilterPopover
              columnId={columnId}
              title={columnId === "city" ? "市区町村" : "都道府県"}
              value={
                columnId === "city"
                  ? cityFilterKeyword
                  : prefectureFilterKeyword
              }
              onApply={(value) => onApplyFilter(columnId, value)}
              onClear={() => onClearFilter(columnId)}
              onClose={() => setActiveFilterColumn(null)}
            />
          ) : (
            <ColumnFilterPopover
              columnId={columnId}
              title={t(SORT_FIELD_LABEL_KEYS_TRANS[columnId] || columnId)}
              options={options}
              onApply={(checkedValues) =>
                onApplyFilter(columnId, checkedValues)
              }
              onClear={() => onClearFilter(columnId)}
              onClose={() => setActiveFilterColumn(null)}
            />
          ))}
      </div>
    );
  };

  const getColHeaderClass = (columnId: ColumnId) => {
    switch (columnId) {
      case "name":
        return "col-th facility-name";
      case "average_rating":
      case "total_review_count":
      case "health_index":
        return "col-th score center";
      case "profile_views":
      case "profile_views_change":
        return "col-th profile-views text-right";
      case "direction_search":
      case "direction_search_change":
        return "col-th direction-search-count text-right";
      case "prefecture":
      case "city":
        return "col-th muni center";
      case "category":
        return "col-th category center";
      case "labels":
        return "col-th labels";
      case "has_lodging":
        return "col-th lodging center";
      case "actions":
        return "col-th actions center";
      default:
        return "col-th center";
    }
  };

  const getColumnLabel = (columnId: ColumnId) => {
    switch (columnId) {
      case "name":
        return t("facility_name");
      case "average_rating":
        return t("average_rating");
      case "health_index":
        return t("health_index");
      case "total_review_count":
        return t("total_review_count");
      case "profile_views":
        return t("profile_views");
      case "profile_views_change":
        return `${t("profile_views")} 增減率`;
      case "direction_search":
        return t("direction_search_count");
      case "direction_search_change":
        return `${t("direction_search_count")} 增減率`;
      case "prefecture":
        return t("prefecture");
      case "city":
        return t("city");
      case "category":
        return t("category");
      case "labels":
        return t("labels");
      case "has_lodging":
        return t("has_lodging");
      case "actions":
        return "";
      default:
        return columnId;
    }
  };

  return (
    <div className="row-th refactored-facility-list-header">
      {visibleColumns.map((columnId) => (
        <div key={`header-${columnId}`} className={getColHeaderClass(columnId)}>
          <div className="spacing header-cell-inner">
            {createSortButton(columnId, getColumnLabel(columnId))}
            {renderFilterTrigger(columnId)}
          </div>
        </div>
      ))}
    </div>
  );
};

export default RefactoredFacilityTableHeaderFilter;
